"use client"

// Inspired by react-hot-toast library
import { useState, useEffect, type ReactNode } from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 3
/** How long auto-dismiss toasts are visible before closing. */
const TOAST_DISPLAY_DURATION = 5000
/** How long after close to keep the toast in state (for exit animation). */
const TOAST_REMOVE_DELAY = 300

/** Variants that require manual dismissal (no auto-dismiss). */
const PERSISTENT_TOAST_VARIANTS = new Set(['destructive', 'warning'])

type ToasterToast = ToastProps & {
  id: string
  title?: ReactNode
  description?: ReactNode
  action?: ToastActionElement
  progressStyle?: 'fill' | 'underline'
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function clearToastTimeout(toastId: string) {
  const timeout = toastTimeouts.get(toastId)
  if (timeout) {
    clearTimeout(timeout)
    toastTimeouts.delete(toastId)
  }
}

function scheduleRemoval(toastId: string) {
  if (toastTimeouts.has(toastId)) return

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({ type: "REMOVE_TOAST", toastId })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? { ...t, open: false }
            : t
        ),
      }
    }

    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return { ...state, toasts: [] }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  // Schedule deferred removal when toasts are dismissed (side effect kept out of reducer)
  if (action.type === "DISMISS_TOAST") {
    if (action.toastId) {
      scheduleRemoval(action.toastId)
    } else {
      memoryState.toasts.forEach((t) => scheduleRemoval(t.id))
    }
  }

  // Clear pending timeouts when toasts are removed to prevent leaks
  if (action.type === "REMOVE_TOAST") {
    if (action.toastId === undefined) {
      toastTimeouts.forEach((timeout) => clearTimeout(timeout))
      toastTimeouts.clear()
    } else {
      clearToastTimeout(action.toastId)
    }
  }

  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => listener(memoryState))
}

type Toast = Omit<ToasterToast, "id">

function isPersistentToast(props: Pick<Toast, "variant" | "action">) {
  return PERSISTENT_TOAST_VARIANTS.has(props.variant ?? '') || props.action != null
}

function toast({ ...props }: Toast) {
  const id = genId()

  // Error/warning toasts and toasts with action buttons persist until dismissed.
  // Success/info toasts auto-dismiss in 5s unless caller overrides duration.
  const duration = props.duration ?? (isPersistentToast(props) ? Infinity : TOAST_DISPLAY_DURATION)

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      duration,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
        props.onOpenChange?.(open)
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = useState<State>(memoryState)

  useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  // Clear toasts on Turbo page navigations so they don't leak across pages
  useEffect(() => {
    const clearToasts = () => dispatch({ type: "REMOVE_TOAST" })
    document.addEventListener("turbo:before-visit", clearToasts)
    return () => document.removeEventListener("turbo:before-visit", clearToasts)
  }, [])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast, isPersistentToast }
